import { useState } from 'react';
import {
  Modal, ModalPortal, ModalOverlay, ModalContent, ModalHeader, ModalTitle, ModalDescription, Input,
} from '@klaro7/ui';

import { resolveUploadUrl } from '@/lib/uploads';
import { useAdminProfessionals } from '@/features/professionals/hooks/useAdminProfessionals';
import { useOpenThread } from '../hooks/useSupport';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the opened/existing conversation id after a professional is picked. */
  onCreated: (conversationId: string) => void;
}

export function NewSupportThreadModal({ open, onOpenChange, onCreated }: Props) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useAdminProfessionals({ limit: 100 });
  const openThread = useOpenThread();

  const professionals = data?.data ?? [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? professionals.filter((p) => {
        const name = `${p.userId?.profile?.firstName ?? ''} ${p.userId?.profile?.lastName ?? ''}`.toLowerCase();
        return name.includes(q) || (p.title ?? '').toLowerCase().includes(q);
      })
    : professionals;

  const pick = (proId: string) => {
    if (openThread.isPending) return;
    openThread.mutate(proId, {
      onSuccess: (conv) => {
        onCreated(conv.id);
        onOpenChange(false);
        setSearch('');
      },
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent className="max-w-md">
          <ModalHeader>
            <ModalTitle>Nuovo thread di supporto</ModalTitle>
            <ModalDescription>Scegli un professionista per avviare una conversazione.</ModalDescription>
          </ModalHeader>

          <div className="mt-3">
            <Input
              placeholder="Cerca per nome o titolo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="mt-3 max-h-80 overflow-y-auto rounded-xl border border-neutral-200">
            {isLoading ? (
              <p className="p-4 text-sm text-neutral-400">Caricamento…</p>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-neutral-400">Nessun professionista trovato</p>
            ) : (
              filtered.map((p) => {
                const name = `${p.userId?.profile?.firstName ?? ''} ${p.userId?.profile?.lastName ?? ''}`.trim() || 'Professionista';
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pick(p.id)}
                    disabled={openThread.isPending}
                    className="flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-3 text-left last:border-b-0 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-neutral-100">
                      {p.userId?.profile?.avatar && (
                        <img src={resolveUploadUrl(p.userId.profile.avatar)} alt="" className="h-9 w-9 object-cover" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold text-neutral-900">{name}</p>
                      {p.title && <p className="truncate text-[11px] text-neutral-400">{p.title}</p>}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {openThread.isError && (
            <p className="mt-2 text-[11px] text-red-600">Impossibile avviare il thread. Riprova.</p>
          )}
        </ModalContent>
      </ModalPortal>
    </Modal>
  );
}
